import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import VideoCard from '@/components/feature/VideoCard';
import { getHotVideos, searchVideos } from '@/lib/videoData';
import { useSeo } from '@/hooks/useSeo';

export default function Search() {
  const [searchParams, setSearchParams] = useSearchParams();
  const keyword = searchParams.get('q') ?? '';
  const [input, setInput] = useState(keyword);
  const [loading, setLoading] = useState(false);

  const results = searchVideos(keyword);
  const hot = getHotVideos(6);

  useSeo({
    title: keyword ? `“${keyword}”的搜索结果 - 桃子视频` : '搜索视频 - 桃子视频',
    description: keyword
      ? `在桃子视频中搜索“${keyword}”，找到 ${results.length} 个相关视频结果，涵盖电影、电视剧、综艺等。`
      : '在桃子视频中搜索你感兴趣的影视内容，涵盖电影、电视剧、综艺等多种类别。',
    keywords: `桃子视频,搜索,${keyword || '影视搜索'},高清视频`,
  });

  useEffect(() => {
    setInput(keyword);
  }, [keyword]);

  useEffect(() => {
    if (!keyword) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const timer = setTimeout(() => setLoading(false), 260);
    return () => clearTimeout(timer);
  }, [keyword]);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const q = input.trim();
    setSearchParams(q ? { q } : {});
  };

  return (
    <div className="min-h-screen bg-background-950">
      <Navbar />
      <main className="mx-auto max-w-7xl px-4 py-8 md:px-8">
        <div className="rounded-lg border border-background-800/80 bg-gradient-to-r from-primary-900/25 to-background-900 p-5 md:p-7">
          <h1 className="font-heading text-xl font-bold text-foreground-50 md:text-2xl">
            搜索视频
          </h1>
          <form onSubmit={submit} className="mt-4 flex flex-col gap-3 sm:flex-row">
            <div className="flex flex-1 items-center gap-2 rounded-md border border-background-700 bg-background-950 px-4 py-2.5">
              <i className="ri-search-line text-foreground-400" />
              <input
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="输入关键词，如电影、电视剧、综艺名称"
                className="w-full bg-transparent text-sm text-foreground-50 placeholder:text-foreground-500 focus:outline-none"
                aria-label="搜索关键词"
              />
              {input && (
                <button
                  type="button"
                  onClick={() => setInput('')}
                  aria-label="清空"
                  className="flex h-6 w-6 items-center justify-center text-foreground-500 hover:text-foreground-200"
                >
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            <button
              type="submit"
              className="flex items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-2.5 text-sm font-medium text-background-50 transition-colors hover:bg-primary-600"
            >
              <i className="ri-search-line" />
              搜索
            </button>
          </form>
          {keyword && (
            <p className="mt-3 text-sm text-foreground-400">
              关键词：<span className="text-primary-400">{keyword}</span>
            </p>
          )}
        </div>

        {keyword && (
          <p className="mt-6 text-sm text-foreground-400">
            找到 <span className="text-primary-400">{results.length}</span> 个视频结果
          </p>
        )}

        {loading ? (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <div key={i} className="animate-pulse rounded-lg border border-background-800/70 bg-background-900">
                <div className="aspect-[3/4] w-full rounded-t-lg bg-background-800" />
                <div className="space-y-2 p-3">
                  <div className="h-4 w-3/4 rounded bg-background-800" />
                  <div className="h-3 w-1/2 rounded bg-background-800" />
                </div>
              </div>
            ))}
          </div>
        ) : results.length > 0 ? (
          <div className="mt-4 grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
            {results.map((video) => (
              <VideoCard key={video.id} video={video} />
            ))}
          </div>
        ) : (
          <div className="mt-8">
            <div className="flex flex-col items-center justify-center rounded-lg border border-dashed border-background-700 bg-background-900/40 py-16 text-center">
              <i className="ri-search-eye-line text-5xl text-foreground-600" />
              <h2 className="mt-4 text-base font-semibold text-foreground-100">
                {keyword ? `没有找到与“${keyword}”相关的视频` : '输入关键词开始搜索'}
              </h2>
              <p className="mt-2 max-w-md text-sm text-foreground-400">
                {keyword
                  ? '换个关键词试试，或者看看下面这些热门内容吧。'
                  : '你可以搜索电影、电视剧、综艺的名称或关键词。'}
              </p>
            </div>

            <div className="mt-8">
              <h2 className="mb-4 flex items-center gap-2 text-lg font-semibold text-foreground-50">
                <i className="ri-fire-line text-primary-400" />
                热门推荐
              </h2>
              <div className="grid grid-cols-2 gap-4 md:grid-cols-3 lg:grid-cols-4">
                {hot.map((video) => (
                  <VideoCard key={video.id} video={video} />
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Link
                to="/category/movie"
                className="rounded-md border border-background-600 px-4 py-2 text-sm text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                浏览电影
              </Link>
              <Link
                to="/category/tv"
                className="rounded-md border border-background-600 px-4 py-2 text-sm text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                浏览电视剧
              </Link>
              <Link
                to="/category/variety"
                className="rounded-md border border-background-600 px-4 py-2 text-sm text-foreground-100 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                浏览综艺
              </Link>
            </div>
          </div>
        )}
      </main>
      <Footer />
    </div>
  );
}